import { isContentEntryRefWithFallback } from '../../types/ContentEntryRefWithFallback';
import { type Optional } from '../../../../types/Optional';
import { type ContentEntryRef } from '../../types/ContentEntryRef';
import type { ContentEntryData, ContentEntryResponse } from '../../types/ContentEntryResponse';
import { useContentEntryQuery } from './useContentEntryQuery';
import { useShouldUseContentEntryFallbacks } from '../../useShouldUseContentEntryFallbacks';

export function useContentEntry<T extends ContentEntryData>(
  ref: Optional<ContentEntryRef<T>, 'contentId' | 'moduleId'> | undefined,
): ContentEntryResponse<T> {
  const useFallbacks = useShouldUseContentEntryFallbacks();
  const queryResult = useContentEntryQuery(ref);
  // Check if the entry exists but is empty string (for text blocks)
  const entry = queryResult.data?.entry;
  let shouldUseFallback = false;
  if (useFallbacks && ref && isContentEntryRefWithFallback(ref)) {
    if (!entry) {
      shouldUseFallback = true;
    } else if (entry.data && entry.data.text) {
      // If all text values are empty strings, treat as missing
      const textArr = Array.isArray(entry.data.text) ? entry.data.text : [entry.data.text];
      if (textArr.every((t) => typeof t === 'string' && t.trim() === '')) {
        shouldUseFallback = true;
      }
    }
  }
  return {
    ref,
    entry:
      !shouldUseFallback && entry
        ? entry
        : ref != null && useFallbacks && isContentEntryRefWithFallback(ref)
          ? {
            contentId: ref.contentId,
            data: ref.fallbackData,
            type: ref.type,
            isOverride: false,
            languageCode: 'en-GB',
            metadata: {},
          }
          : undefined,
    missing: queryResult.isFetched && (!entry || shouldUseFallback),
  } as ContentEntryResponse<T>;
}
